import { hasWorkspacePermission, isManagementReviewer } from './access-control'
import { canAccessRoute } from './route-access'
import type { AccessUser } from '../types/api'

type NavigationAccess = Pick<AccessUser, 'isSuperAdmin' | 'permissions' | 'roles'>

export type NavigationIcon =
  | 'dashboard' | 'tasks' | 'clock' | 'calendar' | 'bell' | 'sparkles' | 'users'
  | 'heart' | 'building' | 'briefcase' | 'file' | 'spreadsheet' | 'chart'
  | 'shield' | 'settings' | 'wrench' | 'qr' | 'mail' | 'mic' | 'presentation'

interface PermissionRequirement {
  permission?: string
  anyOf?: string[]
}

interface NavigationAction extends PermissionRequirement {
  id: string
  label: string
  description: string
  keywords?: string
  personalOnly?: boolean
}

export interface NavigationItem extends PermissionRequirement {
  to: string
  label: string
  icon: NavigationIcon
  keywords?: string
  actions?: NavigationAction[]
}

export interface NavigationGroup {
  id: string
  label: string
  icon: NavigationIcon
  alwaysOpen?: boolean
  items: NavigationItem[]
}

// Keep the original route permissions here. The sidebar and search both consume
// the filtered result, so search cannot accidentally reveal a hidden destination.
const navigationGroups: NavigationGroup[] = [
  {
    id: 'daily', label: 'Daily work', icon: 'dashboard', alwaysOpen: true,
    items: [
      { to: '/', label: 'Dashboard', icon: 'dashboard', keywords: 'home overview' },
      {
        to: '/tasks', label: 'Weekly Tasks', icon: 'tasks', keywords: 'work plan deliverables',
        actions: [{ id: 'add-task', label: 'Add a task', description: 'Open Weekly Tasks to assign or add a task.', anyOf: ['tasks.own.create', 'tasks.manage'], personalOnly: true, keywords: 'create assign' }],
      },
      {
        to: '/attendance', label: 'Attendance', icon: 'clock', anyOf: ['attendance.own.manage', 'attendance.read', 'attendance.manage'],
        actions: [{ id: 'clock-in', label: 'Clock in or out', description: 'Open Attendance to record your work day.', permission: 'attendance.own.manage', keywords: 'time work day' }],
      },
      {
        to: '/leave', label: 'Leave', icon: 'calendar', keywords: 'time off holiday absence',
        actions: [{ id: 'request-leave', label: 'Request leave', description: 'Open Leave, then choose Request leave.', permission: 'leave.own.manage', personalOnly: true, keywords: 'time off holiday' }],
      },
      { to: '/notifications', label: 'Notifications', icon: 'bell', keywords: 'inbox updates alerts' },
      { to: '/ai', label: 'WINBOX AI', icon: 'sparkles', permission: 'ai.use', keywords: 'assistant ask' },
    ],
  },
  {
    id: 'people', label: 'People & Resources', icon: 'users',
    items: [
      { to: '/employees', label: 'Employees', icon: 'users', permission: 'employees.read', keywords: 'staff team directory' },
      { to: '/tprs', label: 'TPRS meetings', icon: 'mic', permission: 'tprs.manage', keywords: 'meeting recordings transcript reviewed notes' },
      { to: '/documents', label: 'Resources', icon: 'file', keywords: 'files documents folders library' },
      { to: '/eap', label: 'EAP', icon: 'heart', permission: 'eap.read', keywords: 'employee assistance cases' },
    ],
  },
  {
    id: 'clients', label: 'Clients & Revenue', icon: 'building',
    items: [
      { to: '/crm', label: 'CRM & Clients', icon: 'building', permission: 'clients.read', keywords: 'contacts organizations followups' },
      { to: '/crm/opportunities', label: 'Opportunities', icon: 'briefcase', permission: 'clients.read', keywords: 'pipeline sales' },
      { to: '/crm/proposals', label: 'Proposals', icon: 'file', permission: 'clients.read' },
      { to: '/programmes', label: 'Programmes', icon: 'briefcase', permission: 'programmes.read', keywords: 'programs editions events' },
      { to: '/invoices', label: 'Invoices', icon: 'spreadsheet', anyOf: ['finance.read', 'finance.manage'], keywords: 'billing payments finance' },
    ],
  },
  {
    id: 'tools', label: 'Office Tools', icon: 'wrench',
    items: [
      { to: '/tools', label: 'Tools Hub', icon: 'wrench', permission: 'tools.use' },
      { to: '/tools/programme-forms', label: 'Programme Forms', icon: 'calendar', permission: 'programmes.forms.read', keywords: 'attendance feedback forms' },
      {
        to: '/qr-studio', label: 'QR Studio', icon: 'qr', permission: 'tools.use',
        actions: [{ id: 'create-qr', label: 'Create a QR code', description: 'Open QR Studio to prepare and verify QR artwork.' }],
      },
      { to: '/outreach', label: 'Batch Email', icon: 'mail', permission: 'outreach.manage', keywords: 'outreach mail campaigns' },
      {
        to: '/tools/document-converter', label: 'Document Converter', icon: 'file', permission: 'tools.use',
        actions: [{ id: 'convert-document', label: 'Convert a document to PDF', description: 'Open Document Converter to choose a file.', keywords: 'word docx image' }],
      },
      {
        to: '/tools/certificates', label: 'Certificate Generator', icon: 'spreadsheet', permission: 'tools.use',
        actions: [{ id: 'generate-certificates', label: 'Generate certificates', description: 'Open Certificate Generator to prepare certificates.' }],
      },
      {
        to: '/tools/slide-studio', label: 'Slide Studio', icon: 'presentation', permission: 'tools.use',
        keywords: 'powerpoint pptx presentation deck slides',
        actions: [{ id: 'create-slides', label: 'Create a slide deck', description: 'Open Slide Studio to draft a PowerPoint from a brief and documents.' }],
      },
      { to: '/tools/signature-stamp', label: 'Signature & Stamp', icon: 'shield', permission: 'tools.use', keywords: 'sign document' },
    ],
  },
  {
    id: 'management', label: 'Reports & Settings', icon: 'settings',
    items: [
      { to: '/reports', label: 'Reports', icon: 'chart', permission: 'reports.read', keywords: 'analytics export csv' },
      { to: '/roles', label: 'Roles & Access', icon: 'shield', permission: 'roles.manage', keywords: 'permissions security' },
      { to: '/settings', label: 'Settings', icon: 'settings', keywords: 'company preferences reminders' },
    ],
  },
]

export function getVisibleNavigation(access: NavigationAccess | null | undefined): NavigationGroup[] {
  const allowed = (requirement: PermissionRequirement) => requirement.anyOf?.length
    ? requirement.anyOf.some((permission) => hasWorkspacePermission(access, permission))
    : hasWorkspacePermission(access, requirement.permission)

  return navigationGroups.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => canAccessRoute(access, item.to))
      .map((item) => ({
        ...item,
        actions: item.actions?.filter((action) => allowed(action) && (!action.personalOnly || !isManagementReviewer(access))),
      })),
  })).filter((group) => group.items.length > 0)
}

// The longest matching destination wins: proposal details belong to Proposals,
// not both Proposals and CRM. A slash boundary avoids matching /tasks-unknown.
export function getActiveNavigationPath(groups: NavigationGroup[], pathname: string): string | undefined {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/'
  return groups.flatMap((group) => group.items)
    .filter((item) => path === item.to || (item.to !== '/' && path.startsWith(`${item.to}/`)))
    .sort((left, right) => right.to.length - left.to.length)[0]?.to
}

export interface NavigationSearchResult {
  id: string
  to: string
  label: string
  description: string
  kind: 'page' | 'action'
  keywords: string
}

export function searchNavigation(groups: NavigationGroup[], query: string): NavigationSearchResult[] {
  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean)
  const results = groups.flatMap((group) => group.items.flatMap((item): NavigationSearchResult[] => [
    {
      id: item.to, to: item.to, label: item.label, description: group.label, kind: 'page',
      keywords: item.keywords ?? '',
    },
    ...(item.actions ?? []).map((action): NavigationSearchResult => ({
      id: action.id, to: item.to, label: action.label, description: action.description, kind: 'action',
      keywords: `${item.label} ${action.keywords ?? ''}`,
    })),
  ]))

  return results.filter((result) => {
    const text = `${result.label} ${result.description} ${result.keywords}`.toLocaleLowerCase()
    return terms.every((term) => text.includes(term))
  })
}
